import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useOffline } from '@/context/OfflineContext';
import {
  Printer,
  MonitorPlay,
  Plus,
  Trash,
  ArrowsClockwise,
  CheckCircle,
  XCircle,
  Warning,
  PaperPlaneTilt,
} from '@phosphor-icons/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { API_URL as __API_BASE__ } from '@/lib/apiBase';
const API_URL = __API_BASE__;

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
const STATION_TYPES = ['main', 'kitchen', 'grill', 'fryer', 'salad', 'pizza', 'bar', 'dessert', 'expo', 'receipt'];

const StatusBadge = ({ status }) => {
  const map = {
    sent: { label: 'Sent', cls: 'bg-green-500/15 text-green-600 border-green-500/30' },
    queued: { label: 'Queued', cls: 'bg-blue-500/15 text-blue-600 border-blue-500/30' },
    fallback: { label: 'Fallback', cls: 'bg-yellow-500/15 text-yellow-600 border-yellow-500/30' },
    failed: { label: 'Failed', cls: 'bg-red-500/15 text-red-600 border-red-500/30' },
  };
  const m = map[status] || map.queued;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${m.cls}`}>{m.label}</span>
  );
};

export const RoutingSettings = ({ outletId }) => {
  const { simulatedOffline, setSimulatedOffline } = useOffline();
  const [printers, setPrinters] = useState([]);
  const [kdsStations, setKdsStations] = useState([]);
  const [printJobs, setPrintJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddPrinter, setShowAddPrinter] = useState(false);
  const [newPrinter, setNewPrinter] = useState({ name: '', station_type: 'main', ip_address: '' });

  const fetchAll = useCallback(async () => {
    if (!outletId) return;
    setLoading(true);
    try {
      const [pr, st, jb] = await Promise.all([
        axios.get(`${API_URL}/api/printers?outlet_id=${outletId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/kds-stations/status?outlet_id=${outletId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/print-jobs?outlet_id=${outletId}&limit=20`, { withCredentials: true }),
      ]);
      setPrinters(pr.data);
      setKdsStations(st.data);
      setPrintJobs(jb.data);
    } catch (e) {
      console.error('Routing fetch error', e);
    } finally {
      setLoading(false);
    }
  }, [outletId]);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 10000); // refresh every 10s for live status
    return () => clearInterval(t);
  }, [fetchAll]);

  const togglePrinter = async (printer) => {
    try {
      await axios.put(
        `${API_URL}/api/printers/${printer.id}`,
        { is_online: !printer.is_online },
        { withCredentials: true }
      );
      toast.success(`${printer.name} ${!printer.is_online ? 'brought online' : 'taken offline'}`);
      fetchAll();
    } catch (e) {
      toast.error('Failed to update printer');
    }
  };

  const deletePrinter = async (printer) => {
    if (!window.confirm(`Delete ${printer.name}?`)) return;
    try {
      await axios.delete(`${API_URL}/api/printers/${printer.id}`, { withCredentials: true });
      toast.success('Printer deleted');
      fetchAll();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const addPrinter = async () => {
    if (!newPrinter.name.trim()) {
      toast.error('Name is required');
      return;
    }
    try {
      await axios.post(
        `${API_URL}/api/printers`,
        { ...newPrinter, outlet_id: outletId, is_fallback: true },
        { withCredentials: true }
      );
      toast.success('Printer added');
      setNewPrinter({ name: '', station_type: 'main', ip_address: '' });
      setShowAddPrinter(false);
      fetchAll();
    } catch (e) {
      toast.error('Failed to add printer');
    }
  };

  const sendHeartbeat = async (stationId, stationName) => {
    try {
      await axios.post(
        `${API_URL}/api/kds-stations/${stationId}/heartbeat`,
        {},
        { withCredentials: true }
      );
      toast.success(`Heartbeat sent for ${stationName}`);
      fetchAll();
    } catch (e) {
      toast.error('Heartbeat failed');
    }
  };

  return (
    <Card
      className="border-2 border-[var(--theme-accent)]/30 bg-[var(--theme-surface)]"
      data-testid="routing-settings"
    >
      <CardHeader className="border-b border-[var(--theme-border)] bg-gradient-to-r from-[var(--theme-accent)]/5 to-transparent">
        <CardTitle className="font-heading text-xl flex items-center gap-3 text-[var(--theme-text)]">
          <div className="p-2 rounded-xl bg-[var(--theme-accent)]/10">
            <PaperPlaneTilt size={24} className="text-[var(--theme-accent)]" />
          </div>
          Order Routing & Printer Failover
          <Button
            size="sm"
            variant="ghost"
            onClick={fetchAll}
            className="ml-auto"
            data-testid="routing-refresh"
          >
            <ArrowsClockwise size={14} className={loading ? 'animate-spin' : ''} />
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-8">
        <p className="text-sm text-[var(--theme-text-muted)] -mt-2">
          When a KDS station goes offline (no heartbeat within 30s), orders for its station auto-route to the configured fallback printer. Toggle printers online/offline below to simulate hardware loss.
        </p>

        {/* Simulate Offline Toggle */}
        <div
          className="rounded-xl border border-[var(--theme-border)] bg-[var(--theme-background)] p-4 flex items-center justify-between"
          data-testid="simulate-offline-row"
        >
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-[var(--theme-text)]">Simulate offline mode</p>
            <p className="text-xs text-[var(--theme-text-muted)] mt-0.5">
              Forces the app to act as if disconnected — useful for training staff on the queue-and-sync flow.
            </p>
          </div>
          <Switch
            checked={simulatedOffline}
            onCheckedChange={(v) => {
              setSimulatedOffline(v);
              toast.info(v ? 'Simulated offline mode ON' : 'Simulated offline mode OFF');
            }}
            data-testid="simulate-offline-toggle"
          />
        </div>

        {/* KDS Stations */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-[var(--theme-text)] flex items-center gap-2">
              <MonitorPlay size={18} /> KDS Stations
            </h4>
            <span className="text-xs text-[var(--theme-text-muted)]">
              {kdsStations.filter((s) => s.online).length} of {kdsStations.length} online
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="kds-status-grid">
            {kdsStations.map((s) => (
              <div
                key={s.id}
                className="rounded-xl border p-3 flex items-center justify-between gap-3"
                style={{
                  borderColor: s.online ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)',
                  backgroundColor: s.online ? 'rgba(34,197,94,0.05)' : 'rgba(239,68,68,0.05)',
                }}
                data-testid={`kds-status-${s.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate text-[var(--theme-text)]">{s.name}</p>
                  <p className="text-[11px] text-[var(--theme-text-muted)] capitalize">
                    {s.station_type} ·{' '}
                    {s.online ? (
                      <span className="text-green-600 font-medium">Online</span>
                    ) : (
                      <span className="text-red-600 font-medium">Offline</span>
                    )}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => sendHeartbeat(s.id, s.name)}
                  className="h-8 text-xs"
                  data-testid={`kds-heartbeat-${s.id}`}
                >
                  Ping
                </Button>
              </div>
            ))}
            {kdsStations.length === 0 && (
              <p className="text-sm text-[var(--theme-text-muted)] col-span-full">
                No KDS stations configured.
              </p>
            )}
          </div>
        </section>

        {/* Printers */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-[var(--theme-text)] flex items-center gap-2">
              <Printer size={18} /> Printers
            </h4>
            <Button
              size="sm"
              onClick={() => setShowAddPrinter((v) => !v)}
              className="bg-[var(--theme-accent)] text-white hover:opacity-90"
              data-testid="add-printer-toggle"
            >
              <Plus size={14} className="mr-1" /> Add Printer
            </Button>
          </div>

          {showAddPrinter && (
            <div
              className="rounded-xl border border-dashed border-[var(--theme-border)] p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3 items-end"
              data-testid="add-printer-form"
            >
              <div>
                <Label className="text-xs">Name</Label>
                <Input
                  value={newPrinter.name}
                  onChange={(e) => setNewPrinter({ ...newPrinter, name: e.target.value })}
                  placeholder="Kitchen Printer 1"
                  data-testid="printer-name-input"
                />
              </div>
              <div>
                <Label className="text-xs">Station Type</Label>
                <Select
                  value={newPrinter.station_type}
                  onValueChange={(v) => setNewPrinter({ ...newPrinter, station_type: v })}
                >
                  <SelectTrigger data-testid="printer-station-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATION_TYPES.map((s) => (
                      <SelectItem key={s} value={s} className="capitalize">
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">IP / Connection (mock)</Label>
                <Input
                  value={newPrinter.ip_address}
                  onChange={(e) => setNewPrinter({ ...newPrinter, ip_address: e.target.value })}
                  placeholder="192.168.1.50"
                  data-testid="printer-ip-input"
                />
              </div>
              <Button onClick={addPrinter} className="bg-[var(--theme-accent)] text-white" data-testid="printer-save-btn">
                Save
              </Button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="printers-grid">
            {printers.map((p) => (
              <div
                key={p.id}
                className="rounded-xl border p-3"
                style={{ borderColor: 'var(--theme-border)', backgroundColor: 'var(--theme-background)' }}
                data-testid={`printer-${p.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate text-[var(--theme-text)]">{p.name}</p>
                    <p className="text-[11px] text-[var(--theme-text-muted)] capitalize">
                      {p.station_type} {p.ip_address ? `· ${p.ip_address}` : ''}
                    </p>
                  </div>
                  <button
                    onClick={() => deletePrinter(p)}
                    className="text-[var(--theme-text-muted)] hover:text-red-500 p-1"
                    data-testid={`printer-delete-${p.id}`}
                  >
                    <Trash size={14} />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {p.is_online ? (
                      <CheckCircle size={14} className="text-green-600" weight="fill" />
                    ) : (
                      <XCircle size={14} className="text-red-600" weight="fill" />
                    )}
                    <span className="text-xs">{p.is_online ? 'Online' : 'Offline'}</span>
                  </div>
                  <Switch
                    checked={!!p.is_online}
                    onCheckedChange={() => togglePrinter(p)}
                    data-testid={`printer-toggle-${p.id}`}
                  />
                </div>
                {p.is_fallback && (
                  <p className="text-[10px] text-[var(--theme-text-muted)] mt-2 italic">
                    Fallback target for this station
                  </p>
                )}
              </div>
            ))}
            {printers.length === 0 && (
              <p className="text-sm text-[var(--theme-text-muted)] col-span-full">
                No printers yet. Add one to enable failover routing.
              </p>
            )}
          </div>
        </section>

        {/* Print Job Log */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h4 className="font-medium text-[var(--theme-text)] flex items-center gap-2">
              <Warning size={18} /> Recent Routing Log
            </h4>
            <span className="text-xs text-[var(--theme-text-muted)]">last 20 events</span>
          </div>
          <div
            className="rounded-xl border border-[var(--theme-border)] divide-y divide-[var(--theme-border)] max-h-96 overflow-y-auto"
            data-testid="print-jobs-log"
          >
            {printJobs.map((j) => (
              <div key={j.id} className="p-3 flex items-center gap-3 text-sm" data-testid={`print-job-${j.id}`}>
                <div className="flex-shrink-0">
                  <StatusBadge status={j.status} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-[var(--theme-text)]">
                    #{j.order_number} → <span className="capitalize">{j.station}</span>
                  </p>
                  <p className="text-[11px] text-[var(--theme-text-muted)]">
                    {j.target_type === 'kds' ? 'KDS' : j.target_type === 'printer' ? 'Printer' : 'Unrouted'}: {j.target_name}
                    {j.reason ? ` · ${j.reason}` : ''}
                  </p>
                </div>
                <span className="text-[11px] text-[var(--theme-text-muted)] flex-shrink-0">
                  {j.created_at ? new Date(j.created_at).toLocaleTimeString() : ''}
                </span>
              </div>
            ))}
            {printJobs.length === 0 && (
              <p className="text-sm text-[var(--theme-text-muted)] p-4 text-center">
                No routing events yet. Place an order to see routing in action.
              </p>
            )}
          </div>
        </section>
      </CardContent>
    </Card>
  );
};

export default RoutingSettings;
